import immutableUpdate, {extend, Spec as ISpec, CustomCommands} from 'immutability-helper'
import {useReducer} from 'external/react'
import triggerHooks from 'external/deep-diff'

export type LoadableList<T> = {
  loading: boolean
  initialized: boolean
  items: Array<T> 
  refreshes: number
}

export type Paging = false | {
  currentPage: number
  recordCount: number
  pageSize: number
}

export function createLoadableList<T> (override?: Partial<LoadableList<T>>): LoadableList<T> {
  return {loading: false, initialized: false, items: [], refreshes:0, ...(override || {})}
}


export interface ExpandedCommands {
  $shallow?: Parameters<typeof shallowHandler>[1],
  $auto?: string
  $autoArray?: string
  $startLoading?: LoadableList<unknown>
  $updateLoadableList?: LoadableList<unknown>
}

export type Spec<T> = ISpec<T, CustomCommands<ExpandedCommands>>

extend('$shallow', shallowHandler)

extend('$auto', autoHandler)

extend('$autoArray', autoArrayHandler)

extend('$startLoading', startLoadingHandler)

extend('$updateLoadableList', updateLoadableListHandler)

// extend<CustomCommands<ExpandedCommands>>('$startLoading', startLoadingHandler)

function shallowHandler<T> (value: Spec<T>,  object: T) {
  const commands = Object.entries(value || {}).reduce((obj, [key, value]) => ({...obj, [key]: {$set: value}}), {}) as Spec<T>
  return expandedUpdate(object, commands)
}

function autoHandler<T>(value: Spec<T>, object: T) {
  return object ?
    expandedUpdate(object, value):
    expandedUpdate({} as T, value);
}

function autoArrayHandler<T> (value: Spec<T>, object: T) {
  return object
    ? expandedUpdate(object, value)
    : expandedUpdate([] as T, value)
}

function startLoadingHandler<T> (_:any, object: T) {
  const updateProps = {initialized: {$set: false}, loading: {$set: true}, items: {$set: []}} as unknown as Spec<T>
  return expandedUpdate<T>(object, updateProps)
}

function updateLoadableListHandler<T> (value: Array<T>, object: T) {
  const updateProps = {initialized: {$set: true}, loading: {$set: false}, items: {$set: value}} as unknown as Spec<T>
  return expandedUpdate(object, updateProps)
}

function expandedUpdate<T>(object: T, spec: ISpec<T, CustomCommands<ExpandedCommands>>) {
  return immutableUpdate(object, spec)
}


type ReducerAction<T> = {
  type?: string
  value: {
    update: (spec: Spec<T>) => void
    command: Spec<T> | Array<Spec<T>>
    resolve: (newState: T) => void
  }
}

function immutableReducer<T> (state:T, action: ReducerAction<T>) {
  const {command, update, resolve} = action.value
  const commands = Array.isArray(command) ? command : [command]
  const newState = commands.reduce(expandedUpdate, state)
  if (action.type) {
    triggerHooks(action.type, newState, state, update)
  }
  resolve(newState)
  return newState
}

function useImmutableState<T>(initialState:T, id?: string) {
  const [state, dispatch] = useReducer(immutableReducer, initialState)

  const update = (command: Spec<T>) => {
    return new Promise<T>((resolve) => {
      dispatch({type: id, value: {command, update, resolve}})
    })
  }

  return {state, update}
}

export {expandedUpdate as default, useImmutableState} 